import { api } from "./api";

type UploadOCRQueryResultResponse = {
  filename: string;
  job_id: number;
  textract_request_id: number;
  message: string;
};

type UploadOCRQueryResultArgs = {
  fileId: string;
  formData: FormData;
};

// No textract_request_id here, unlike query result, since the Celery
// task creates it asynchronously and it doesn't exist yet at response
// time.
type UploadOCRRatedAgeResponse = {
  filename: string;
  job_id: number;
  message: string;
};

type UploadOCRRatedAgeArgs = {
  msaId?: string | number;
  costProjectionId?: string | number;
  documentId: number;
};

export type TextractRequestStatus =
  | "pending"
  | "in_progress"
  | "succeeded"
  | "failed"
  | "partial_success";

export type TextractRequest = {
  id: number;
  job_id: string;
  job_type: string;
  status: TextractRequestStatus;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  created: string;
  s3_filepath: string | null;
};

type GetTextractRequestsByFileArgs = {
  fileId: number;
  // Scopes to one feature's own jobs, since a file can have unrelated Textract jobs running too.
  jobType?: string;
};

export const rreProfileIngestionApi = api.injectEndpoints({
  endpoints: (build) => ({
    uploadOCRQueryResult: build.mutation<
      UploadOCRQueryResultResponse,
      UploadOCRQueryResultArgs
    >({
      query: ({ fileId, formData }) => {
        // append file_id to formData
        formData.append("file_id", fileId);

        return {
          url: "api/v1/ocr-query-result/upload/",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { fileId }) => [
        { type: "TextractRequest", id: `file-${fileId}` },
      ],
    }),

    uploadOCRRatedAge: build.mutation<
      UploadOCRRatedAgeResponse,
      UploadOCRRatedAgeArgs
    >({
      query: ({ msaId, costProjectionId, documentId }) => ({
        url: "api/v1/ocr-rated-age/upload/",
        method: "POST",
        body: {
          document_id: documentId,
          msa_id: msaId,
          cost_projection_id: costProjectionId,
        },
      }),
      // RatedAge/MSA/MCP/LCP are invalidated later by RatedAgeOCRStatus
      // and RatedAgeOCRWatcher, once parsing actually finishes.
      invalidatesTags: ["BackgroundJobs"],
    }),

    getTextractRequestsByFile: build.query<
      TextractRequest[],
      GetTextractRequestsByFileArgs
    >({
      query: ({ fileId, jobType }) => ({
        url: `api/v1/textract-requests/`,
        params: {
          file_id: fileId,
          ...(jobType ? { job_type: jobType } : {}),
        },
      }),
      transformResponse: (response: { results: TextractRequest[] }) =>
        response.results,
      providesTags: (_result, _error, { fileId }) => [
        { type: "TextractRequest", id: `file-${fileId}` },
      ],
    }),
  }),
});

export const {
  useUploadOCRQueryResultMutation,
  useUploadOCRRatedAgeMutation,
  useGetTextractRequestsByFileQuery,
} = rreProfileIngestionApi;
